<template>
  <component :is="pageLayout">
    <template #content>
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 class="h4 mb-1">Comptage inventaire</h1>
          <small v-if="inventory" class="text-muted">
            {{ inventory.reference }} · {{ inventory.type === 'global' ? 'Global' : 'Partiel' }} ·
            {{ scopeLabel(inventory) }}
          </small>
          <small v-else class="text-muted">
            Chargement du brouillon...
          </small>
        </div>

        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="goBack">
            <i class="fa fa-arrow-left me-1"></i>
            {{ backButtonLabel }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="isLoadingInventory"
            @click="fetchInventory"
          >
            <i class="fa fa-rotate-right me-1"></i>
            Recharger
          </button>
        </div>
      </div>

      <div v-if="globalError" class="alert alert-danger mb-3 py-2">
        {{ globalError }}
      </div>
      <div v-if="globalSuccess" class="alert alert-success mb-3 py-2">
        {{ globalSuccess }}
      </div>

      <div v-if="inventory" class="mb-3">
        <div class="d-flex justify-content-between small mb-1">
          <span>Produits restants : {{ remainingCount }}</span>
          <span>Produits traités : {{ processedCount }}</span>
        </div>
        <div class="progress" style="height: 6px;">
          <div class="progress-bar bg-success" role="progressbar" :style="{ width: progressPercent + '%' }"></div>
        </div>
      </div>

      <div v-if="isLoadingInventory" class="alert alert-info py-2">
        Chargement de l’inventaire...
      </div>

      <div v-else-if="inventory">
        <div class="inventory-search-panel mb-3" data-test="inventory-search-panel">
          <div class="inventory-search-field">
            <i class="fa fa-search inventory-search-icon"></i>
            <input
              v-model="inventorySearch"
              type="text"
              class="form-control form-control-sm inventory-search-input"
              placeholder="Rechercher un produit, code ou code-barres..."
              aria-label="Rechercher un produit à compter"
            />
            <button
              v-if="hasInventorySearch"
              type="button"
              class="inventory-search-clear"
              title="Effacer la recherche"
              aria-label="Effacer la recherche"
              @click="clearInventorySearch"
            >
              <i class="fa fa-xmark"></i>
            </button>
          </div>
          <small v-if="hasInventorySearch" class="inventory-search-summary">
            {{ searchResultCount }} produit(s) affiché(s)
          </small>
        </div>

        <div v-if="showOneRayonAtTime && availableRayons.length" class="rayon-focus-control mb-3">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="availableRayons.length <= 1"
            @click="moveActiveRayon(-1)"
          >
            <i class="fa fa-chevron-left me-1"></i>
            Précédent
          </button>

          <span class="rayon-focus-label">
            <span class="rayon-color-dot" :style="{ backgroundColor: rayonColorForGroup(activeRayon) }"></span>
          </span>

          <select v-model="activeRayon" class="form-select form-select-sm">
            <option v-for="rayon in availableRayons" :key="rayon" :value="rayon">
              {{ rayon || 'Non défini' }}
            </option>
          </select>

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="availableRayons.length <= 1"
            @click="moveActiveRayon(1)"
          >
            Suivant
            <i class="fa fa-chevron-right ms-1"></i>
          </button>
        </div>

        <div
          v-if="hasInventorySearch && searchResultCount === 0"
          class="inventory-search-empty text-center text-muted my-4"
        >
          <i class="fa fa-search mb-2"></i>
          <div>Aucun produit ne correspond à cette recherche.</div>
        </div>

        <div v-for="(items, rayon) in visibleGroupedItems" :key="rayon" class="mb-4">
          <h5 class="mb-2 rayon-group-title">
            <span class="rayon-color-dot" :style="{ backgroundColor: rayonColorForGroup(rayon) }"></span>
            <i class="fa fa-layer-group me-1"></i>
            Rayon : {{ rayon || 'Non défini' }}
          </h5>

          <div class="row g-2">
            <div v-for="item in items" :key="item.id" class="col-md-4 col-sm-6">
              <div
                class="card inventory-card h-100"
                :class="{
                  'inventory-card-saving': savingItemId === item.id,
                  'inventory-card-processed': isItemProcessed(item),
                }"
                @click="setActiveItem(item)"
              >
                <div class="card-body inventory-card-body">
                  <div class="inventory-product-photo" aria-hidden="true">
                    <img
                      :src="productPhoto(item)"
                      :alt="productName(item)"
                      loading="lazy"
                      @error="markProductImageBroken(item)"
                    />
                  </div>

                  <div class="inventory-card-content">
                    <div class="inventory-card-topline">
                      <div>
                        <div class="fw-semibold small inventory-product-title">
                          {{ productName(item) }}
                          <span v-if="isItemProcessed(item)" class="badge bg-light text-success border">
                            Traité
                          </span>
                        </div>
                        <div class="text-muted extra-small">
                          Code : {{ productSku(item) || '-' }}<br />
                          Stock théorique : {{ item.stock_quantity }}
                        </div>
                      </div>
                    </div>

                  </div>

                  <div class="inventory-count-controls">
                    <label class="form-label small mb-1">
                      Quantité réelle
                    </label>
                    <input
                      :ref="(element) => setQuantityInputRef(item.id, element)"
                      v-model.number="realQuantities[item.id]"
                      type="number"
                      min="0"
                      class="form-control form-control-sm"
                      placeholder="Qté comptée"
                      :disabled="savingItemId === item.id"
                      @focus="setActiveItem(item)"
                      @input="setActiveItem(item)"
                      @keyup.enter="validateItem(item)"
                    />

                    <transition name="validate-state" mode="out-in">
                      <span
                        v-if="isItemSavedAndUnchanged(item)"
                        :key="`saved-${item.id}`"
                        class="inventory-saved-badge"
                      >
                        <i class="fa fa-check-circle"></i>
                        Enregistré
                      </span>

                      <button
                        v-else
                        :key="`validate-${item.id}`"
                        type="button"
                        class="btn btn-sm btn-success inventory-validate-button"
                        :title="`Valider la quantité (${validateShortcutLabel})`"
                        :disabled="savingItemId === item.id || !canValidateItem(item)"
                        @click.stop="validateItem(item)"
                      >
                        <i class="fa" :class="savingItemId === item.id ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                        <span>Valider</span>
                      </button>
                    </transition>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="items.length === 0" class="text-muted small">
              Tous les produits de ce rayon ont été traités.
            </div>
          </div>
        </div>

        <div v-if="remainingCount === 0" class="text-center my-4 inventory-complete">
          <h5 class="mb-2">Comptage terminé.</h5>
          <p class="text-muted mb-3">Un gérant peut maintenant clôturer cet inventaire depuis le backoffice.</p>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="goBack">
            <i class="fa fa-arrow-left me-1"></i>
            {{ completeBackButtonLabel }}
          </button>
        </div>
      </div>
    </template>
  </component>
</template>

<script>
import { productImageUrl } from '@/support/productImages';
import { normalizeRayonColor } from '@/utils/rayonColor';

export default {
  name: 'InventoryProcessView',
  data() {
    return {
      inventory: null,
      items: [],
      realQuantities: {},
      savingItemId: null,
      isLoadingInventory: false,
      globalError: '',
      globalSuccess: '',
      inventorySearch: '',
      activeRayon: '',
      activeItemId: null,
      brokenProductImages: {},
      quantityInputRefs: {},
      savedFeedbackItemId: null,
      savedFeedbackTimer: null,
    };
  },
  computed: {
    inventorySettings() {
      return this.$store.getters['userSettings/inventory'] || {};
    },
    hideProcessedProducts() {
      return this.inventorySettings.hide_processed_products !== false;
    },
    showOneRayonAtTime() {
      return this.inventorySettings.show_one_rayon_at_time === true;
    },
    isMacOS() {
      if (typeof navigator === 'undefined') return false;

      const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';

      return /mac|iphone|ipad|ipod/i.test(platform);
    },
    validateShortcutLabel() {
      return `${this.isMacOS ? 'Cmd' : 'Ctrl'}+Entrée`;
    },
    displayItems() {
      if (!this.hideProcessedProducts) {
        return this.items;
      }

      return this.items.filter((item) => (
        !this.isItemProcessed(item)
        || Number(item.id) === Number(this.savedFeedbackItemId)
      ));
    },
    normalizedInventorySearch() {
      return this.normalizeSearchText(this.inventorySearch);
    },
    hasInventorySearch() {
      return this.normalizedInventorySearch.length > 0;
    },
    filteredDisplayItems() {
      if (!this.hasInventorySearch) {
        return this.displayItems;
      }

      return this.displayItems.filter((item) => this.inventorySearchText(item).includes(this.normalizedInventorySearch));
    },
    searchResultCount() {
      return this.filteredDisplayItems.length;
    },
    groupedItems() {
      const groups = {};
      this.filteredDisplayItems.forEach((item) => {
        const key = this.itemRayon(item);
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      });

      Object.keys(groups).forEach((key) => {
        groups[key].sort((a, b) => this.productName(a).localeCompare(this.productName(b)));
      });

      return groups;
    },
    availableRayons() {
      return Object.keys(this.groupedItems);
    },
    visibleGroupedItems() {
      if (!this.showOneRayonAtTime) {
        return this.groupedItems;
      }

      const active = this.activeRayon && this.groupedItems[this.activeRayon]
        ? this.activeRayon
        : this.availableRayons[0];

      return active ? { [active]: this.groupedItems[active] || [] } : {};
    },
    remainingCount() {
      return this.items.filter((item) => !this.isItemProcessed(item)).length;
    },
    processedCount() {
      return this.items.filter((item) => this.isItemProcessed(item)).length;
    },
    progressPercent() {
      if (!this.items.length) return 0;

      return Math.round((this.processedCount / this.items.length) * 100);
    },
    isBackofficeInventoryCount() {
      return this.$route.name === 'InventoryCount';
    },
    pageLayout() {
      return this.isBackofficeInventoryCount ? 'AdminLayout' : 'CashierLayout';
    },
    backButtonLabel() {
      return this.isBackofficeInventoryCount ? 'Détail' : 'Liste';
    },
    completeBackButtonLabel() {
      return this.isBackofficeInventoryCount ? 'Retour au détail' : 'Retour à la liste';
    },
  },
  mounted() {
    this.$store.dispatch('userSettings/load').catch(() => undefined);
    this.fetchInventory();
    window.addEventListener('keydown', this.handleInventoryShortcut);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleInventoryShortcut);

    if (this.savedFeedbackTimer) {
      window.clearTimeout(this.savedFeedbackTimer);
    }
  },
  watch: {
    availableRayons() {
      this.ensureActiveRayon();
    },
    showOneRayonAtTime() {
      this.ensureActiveRayon();
    },
    inventorySearch() {
      this.ensureActiveRayon();
    },
  },
  methods: {
    async fetchInventory() {
      const inventoryId = this.$route.params.id;
      this.isLoadingInventory = true;
      this.globalError = '';
      this.globalSuccess = '';

      try {
        const response = await this.$api.get(`/inventories/${inventoryId}`);
        const inventory = response.data?.data || null;

        if (!inventory || inventory.status !== 'draft') {
          this.inventory = null;
          this.items = [];
          this.globalError = 'Cet inventaire n’est plus disponible pour le comptage.';
          return;
        }

        this.inventory = inventory;
        this.items = inventory.items || [];
        this.realQuantities = {};
        this.items.forEach((item) => {
          this.realQuantities[item.id] = item.real_quantity === null || typeof item.real_quantity === 'undefined'
            ? null
            : Number(item.real_quantity);
        });
        this.ensureActiveRayon();
        this.$nextTick(() => {
          const firstItem = this.firstVisibleUnprocessedItem();
          if (firstItem) this.focusItemInput(firstItem.id);
        });
      } catch (error) {
        console.error('Erreur chargement inventaire', error);
        this.inventory = null;
        this.items = [];
        this.globalError = 'Impossible de charger cet inventaire.';
      } finally {
        this.isLoadingInventory = false;
      }
    },

    goBack() {
      if (this.isBackofficeInventoryCount) {
        this.$router.push({
          name: 'InventoryShow',
          params: { id: this.$route.params.id },
        });
        return;
      }

      this.$router.push({ name: 'CashierInventories' });
    },

    async validateItem(item) {
      if (this.savingItemId === item.id) return;
      this.setActiveItem(item);

      if (!this.canValidateItem(item)) {
        this.globalError = 'Saisissez la quantité réelle avant de valider ce produit.';
        this.focusItemInput(item.id);
        return;
      }

      this.savingItemId = item.id;
      this.globalError = '';
      this.globalSuccess = '';

      try {
        const response = await this.$api.put(`/inventories/${this.inventory.id}/items/${item.id}/count`, {
          real_quantity: Number(this.realQuantities[item.id]),
        });
        const savedItem = response.data?.data || null;
        if (savedItem) {
          this.replaceItem(savedItem);
          this.realQuantities[savedItem.id] = Number(savedItem.real_quantity);
        }

        this.showSavedFeedback(item.id);
        window.setTimeout(() => {
          this.focusNextItem(item);
        }, 180);
      } catch (error) {
        const errors = error.response?.data?.errors || null;
        this.globalError = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible d’enregistrer cette quantité.';
      } finally {
        this.savingItemId = null;
      }
    },

    replaceItem(savedItem) {
      const index = this.items.findIndex((item) => Number(item.id) === Number(savedItem.id));
      if (index === -1) return;

      this.items.splice(index, 1, {
        ...this.items[index],
        ...savedItem,
      });
    },

    isItemProcessed(item) {
      return item.real_quantity !== null && typeof item.real_quantity !== 'undefined';
    },

    hasCountedQuantity(item) {
      const value = this.realQuantities[item.id];

      return value !== null
        && value !== ''
        && typeof value !== 'undefined'
        && !Number.isNaN(Number(value));
    },

    canValidateItem(item) {
      return this.hasCountedQuantity(item) && Number(this.realQuantities[item.id]) >= 0;
    },

    isItemSavedAndUnchanged(item) {
      if (!this.isItemProcessed(item) || !this.hasCountedQuantity(item)) return false;

      return Number(this.realQuantities[item.id]) === Number(item.real_quantity);
    },

    showSavedFeedback(itemId) {
      this.savedFeedbackItemId = itemId;

      if (this.savedFeedbackTimer) {
        window.clearTimeout(this.savedFeedbackTimer);
      }

      this.savedFeedbackTimer = window.setTimeout(() => {
        if (Number(this.savedFeedbackItemId) === Number(itemId)) {
          this.savedFeedbackItemId = null;
        }
      }, 850);
    },

    setQuantityInputRef(itemId, element) {
      if (element) {
        this.quantityInputRefs[itemId] = element;
      } else {
        delete this.quantityInputRefs[itemId];
      }
    },

    focusItemInput(itemId) {
      this.$nextTick(() => {
        const input = this.quantityInputRefs[itemId];
        if (!input || typeof input.focus !== 'function') return;

        input.focus();
        if (typeof input.select === 'function') {
          input.select();
        }
      });
    },

    setActiveItem(item) {
      this.activeItemId = item?.id || null;
    },

    handleInventoryShortcut(event) {
      if (event.defaultPrevented || event.repeat) return;

      const primaryPressed = this.isMacOS
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;

      if (!primaryPressed || event.altKey || event.shiftKey || String(event.key).toLowerCase() !== 'enter') {
        return;
      }

      const item = this.activeItemId
        ? this.items.find((candidate) => Number(candidate.id) === Number(this.activeItemId))
        : this.firstVisibleUnprocessedItem();

      if (!item) return;

      event.preventDefault();
      event.stopPropagation();
      this.validateItem(item);
    },

    firstVisibleUnprocessedItem() {
      return Object.values(this.visibleGroupedItems)
        .flat()
        .find((item) => !this.isItemProcessed(item)) || null;
    },

    ensureActiveRayon() {
      if (!this.availableRayons.length) {
        this.activeRayon = '';
        return;
      }

      if (!this.activeRayon || !this.availableRayons.includes(this.activeRayon)) {
        this.activeRayon = this.availableRayons[0];
      }
    },

    moveActiveRayon(step) {
      if (this.availableRayons.length <= 1) return;

      const currentIndex = Math.max(0, this.availableRayons.indexOf(this.activeRayon));
      const nextIndex = (currentIndex + step + this.availableRayons.length) % this.availableRayons.length;
      this.activeRayon = this.availableRayons[nextIndex];
    },

    clearInventorySearch() {
      this.inventorySearch = '';
      this.ensureActiveRayon();
    },

    normalizeSearchText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    },

    inventorySearchText(item) {
      return [
        this.productName(item),
        this.productSku(item),
        this.productBarcode(item),
      ]
        .map((value) => this.normalizeSearchText(value))
        .filter(Boolean)
        .join(' ');
    },

    itemRayon(item) {
      return item?.rayon || item?.product?.rayon || 'Sans rayon';
    },

    itemRayonColor(item) {
      return normalizeRayonColor(item?.rayon_color || item?.product?.rayon_color);
    },

    rayonColorForGroup(rayon) {
      const item = this.groupedItems[rayon]?.[0];

      return this.itemRayonColor(item);
    },

    unprocessedItems() {
      return this.items.filter((item) => !this.isItemProcessed(item));
    },

    focusNextItem(currentItem) {
      const currentItemId = Number(currentItem.id);
      const currentIndex = this.items.findIndex((item) => Number(item.id) === currentItemId);
      const itemAfterCurrent = this.unprocessedItems()
        .find((item) => this.items.findIndex((candidate) => Number(candidate.id) === Number(item.id)) > currentIndex);

      if (!this.showOneRayonAtTime) {
        const nextItem = itemAfterCurrent || this.unprocessedItems()[0] || null;
        if (nextItem) {
          this.focusItemInput(nextItem.id);
        }
        return;
      }

      const currentRayon = this.itemRayon(currentItem);
      const sameRayonNext = this.unprocessedItems()
        .filter((item) => this.itemRayon(item) === currentRayon)
        .find((item) => this.items.findIndex((candidate) => Number(candidate.id) === Number(item.id)) > currentIndex);

      if (sameRayonNext) {
        this.activeRayon = currentRayon;
        this.focusItemInput(sameRayonNext.id);
        return;
      }

      const unprocessedRayons = [...new Set(this.unprocessedItems().map((item) => this.itemRayon(item)))];
      const currentRayonIndex = Math.max(0, unprocessedRayons.indexOf(currentRayon));
      const nextRayon = unprocessedRayons.find((rayon, index) => index > currentRayonIndex) || unprocessedRayons[0];
      const nextItem = nextRayon
        ? this.unprocessedItems().find((item) => this.itemRayon(item) === nextRayon)
        : null;

      if (nextItem) {
        this.activeRayon = nextRayon;
        this.focusItemInput(nextItem.id);
      }
    },

    productName(item) {
      return item?.product?.name || item?.product_name || 'Produit';
    },

    productSku(item) {
      return item?.product?.sku || item?.sku || '';
    },

    productBarcode(item) {
      return item?.product?.barcode || item?.barcode || item?.product_barcode || '';
    },

    productPhoto(item) {
      const product = item.product || {};

      return this.brokenProductImages[item?.id] ? productImageUrl(null) : productImageUrl(product);
    },

    markProductImageBroken(item) {
      this.brokenProductImages = {
        ...this.brokenProductImages,
        [item.id]: true,
      };
    },

    productInitials(name) {
      const words = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (!words.length) return 'PR';

      return words
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    },

    scopeLabel(inventory) {
      if (!inventory || inventory.type === 'global') {
        return 'Tous les rayons';
      }

      const rayons = inventory.scope?.rayons || [];
      if (!rayons.length) {
        return 'Rayons sélectionnés';
      }

      return rayons.map((rayon) => rayon.name).join(', ');
    },
  },
};
</script>

<style scoped>
.inventory-card {
  border-radius: 8px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.inventory-search-panel {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.75rem;
}

.inventory-search-field {
  flex: 1 1 320px;
  max-width: 520px;
  position: relative;
}

.inventory-search-icon {
  color: #6c757d;
  left: 0.75rem;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.inventory-search-input {
  padding-left: 2.15rem;
  padding-right: 2.1rem;
}

.inventory-search-clear {
  align-items: center;
  background: transparent;
  border: 0;
  color: #6c757d;
  display: inline-flex;
  height: 1.65rem;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.65rem;
}

.inventory-search-clear:hover,
.inventory-search-clear:focus {
  color: #212529;
}

.inventory-search-summary {
  color: #64748b;
  font-weight: 700;
}

.inventory-search-empty {
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 1.5rem;
}

.inventory-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.05);
}

.inventory-card-saving {
  opacity: 0.7;
}

.inventory-card-processed {
  background: #f6fff8;
  border-color: rgba(25, 135, 84, 0.28);
}

.inventory-card-body {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 86px minmax(0, 1fr) minmax(118px, 136px);
  min-height: 128px;
  padding: 0.75rem;
}

.inventory-product-photo {
  align-items: center;
  align-self: stretch;
  background: #f1f5f9;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  color: #64748b;
  display: inline-flex;
  font-size: 1.1rem;
  font-weight: 900;
  justify-content: center;
  min-height: 104px;
  overflow: hidden;
  width: 86px;
}

.inventory-product-photo img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.inventory-card-content {
  min-width: 0;
}

.inventory-card-topline {
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.inventory-product-title {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.inventory-count-controls {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: flex-start;
}

.inventory-count-controls .form-control {
  min-width: 0;
}

.inventory-validate-button,
.inventory-saved-badge {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 900;
  gap: 0.38rem;
  justify-content: center;
  min-height: 32px;
  min-width: 0;
  width: 100%;
  white-space: nowrap;
}

.inventory-saved-badge {
  background: #ffffff;
  border: 1px solid rgba(25, 135, 84, 0.45);
  box-shadow: 0 8px 20px rgba(25, 135, 84, 0.12);
  color: #198754;
}

.validate-state-enter-active,
.validate-state-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.validate-state-enter-from,
.validate-state-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.96);
}

.rayon-focus-control {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: auto auto minmax(180px, 320px) auto;
  padding: 0.75rem;
}

.rayon-focus-label,
.rayon-group-title {
  align-items: center;
  display: inline-flex;
  gap: 0.45rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.8rem;
  width: 0.8rem;
}

.inventory-complete {
  background: #ffffff;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  padding: 1.25rem;
}

.extra-small {
  font-size: 0.75rem;
}

@media (max-width: 575.98px) {
  .rayon-focus-control {
    grid-template-columns: 1fr;
  }

  .inventory-search-field {
    flex-basis: 100%;
    max-width: 100%;
  }

  .inventory-card-body {
    grid-template-columns: 72px minmax(0, 1fr);
  }

  .inventory-product-photo {
    min-height: 96px;
    width: 72px;
  }

  .inventory-count-controls {
    grid-column: 1 / -1;
  }
}
</style>
